import { useTranslation } from "react-i18next";
import { CONSENT_STATUS_TYPES, CONSENT_STATUS_COLORS } from "../constant/consent";
import { formatConsentEventDate, getConsentActionUserName, mapEventTypeToStatus } from "../utils/eConsentHelper";

/**
 * Maps backend latestConsentEventType to display status type.
 *
 * Delegates to `mapEventTypeToStatus` — the single source of truth. This used to
 * carry its own copy of the map, keyed off `ConsentStatusTypes` instead of
 * `CONSENT_EVENT_TYPES`; since `ConsentStatusTypes` has no LINK_REVOKED member,
 * that key was `undefined` and a link-revoked patient rendered "No consent on
 * file" in the dashboard column while the patient page said "Request link
 * expired". Delegating fixes that divergence and means a new status only has to
 * be added in one place (MVP-29825).
 *
 * @param {string} eventType - The latestConsentEventType from backend
 * @returns {string} The display status type
 */
export const mapEventTypeToDisplayStatus = (eventType) => {
  if (!eventType) {
    return CONSENT_STATUS_TYPES.NO_RECORD;
  }

  return mapEventTypeToStatus(eventType);
};

/**
 * Hook to get consent status configuration
 * @param {string} status - The consent status type
 * @param {Object} consentData - Optional consent data for tooltips (createdAt, user info)
 * @returns {Object} Configuration with text, dotColor, and optional tooltip
 */
export const useConsentStatusConfig = (status, consentData = null) => {
  const { t } = useTranslation();

  const getConfig = () => {
    const consentUserName = consentData ? getConsentActionUserName(consentData, t) : null;
    const formattedDate = consentData?.createdAt ? formatConsentEventDate(consentData.createdAt) : null;

    switch (status) {
      case CONSENT_STATUS_TYPES.NO_RECORD:
        return {
          text: t("consent.status.no_record"),
          dotColor: CONSENT_STATUS_COLORS[CONSENT_STATUS_TYPES.NO_RECORD],
          tooltip: null
        };
      case CONSENT_STATUS_TYPES.PENDING:
        return {
          text: t("consent.status.pending_signature"),
          dotColor: CONSENT_STATUS_COLORS[CONSENT_STATUS_TYPES.PENDING],
          tooltip: consentData
            ? t("consent.tooltips.pending", {
                user: consentUserName,
                date: formattedDate
              })
            : null
        };
      case CONSENT_STATUS_TYPES.RECEIVED:
        return {
          text: t("consent.status.consent_received"),
          dotColor: CONSENT_STATUS_COLORS[CONSENT_STATUS_TYPES.RECEIVED],
          tooltip: consentData
            ? t("consent.tooltips.received", {
                user: consentUserName,
                date: formattedDate
              })
            : null
        };
      case CONSENT_STATUS_TYPES.REVOKED:
        return {
          text: t("consent.status.consent_revoked"),
          dotColor: CONSENT_STATUS_COLORS[CONSENT_STATUS_TYPES.REVOKED],
          tooltip: consentData
            ? t("consent.tooltips.revoked", {
                user: consentUserName,
                date: formattedDate
              })
            : null
        };
      case CONSENT_STATUS_TYPES.EXPIRED:
        return {
          text: t("consent.status.consent_expired"),
          dotColor: CONSENT_STATUS_COLORS[CONSENT_STATUS_TYPES.EXPIRED],
          tooltip: consentData
            ? t("consent.tooltips.expired_by_system", {
                date: formattedDate
              })
            : null
        };
      case CONSENT_STATUS_TYPES.LINK_EXPIRED:
        return {
          text: t("consent.status.request_link_expired"),
          dotColor: CONSENT_STATUS_COLORS[CONSENT_STATUS_TYPES.LINK_EXPIRED],
          tooltip: consentData
            ? t("consent.tooltips.link_expired", {
                date: formattedDate
              })
            : null
        };
      case CONSENT_STATUS_TYPES.DECLINED:
        return {
          text: t("consent.status.consent_declined"),
          dotColor: CONSENT_STATUS_COLORS[CONSENT_STATUS_TYPES.DECLINED],
          tooltip: consentData
            ? t("consent.tooltips.declined", {
                user: consentUserName,
                date: formattedDate
              })
            : null
        };
      default:
        return {
          text: t("consent.status.no_record"),
          dotColor: CONSENT_STATUS_COLORS[CONSENT_STATUS_TYPES.NO_RECORD],
          tooltip: null
        };
    }
  };

  return getConfig();
};
